import React from 'react';
import { PageLayout } from '@voilajsx/uikit/page';
import { Header, Footer, SEO } from '../../../shared/components';

const APP_NAME = import.meta.env.VITE_APP_NAME === 'plotmagic' ? 'PlotMagic' : 'RRD Planners';
const LEGAL_NAME = 'RRD Planners and Constructions';
const ADDRESS = 'Sathya Sai Nagar, Naidupump, Warangal, Telangana, India';
const EMAIL = 'rrdplanners@gmail.com';
const PHONE = '+91 7989358436';

const TermsPage: React.FC = () => {
  return (
    <PageLayout size="full">
      <SEO title={`Terms & Conditions - ${APP_NAME}`} description="Terms and conditions for using our platform" />
      <Header />
      <PageLayout.Content>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl font-bold text-foreground">Terms & Conditions</h1>
              <p className="text-muted-foreground">Last updated: April 2026</p>
            </div>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  This website is operated by <strong>{LEGAL_NAME}</strong>, located at {ADDRESS}. By accessing and using {APP_NAME} (the "Platform"), you accept and agree to be bound by these Terms & Conditions. If you do not agree, please do not use this service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">2. Services</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {APP_NAME} is a credit-based SaaS platform that provides professional cadastral plot map generation services. Users can create, customize, and download A4 registration-ready plot maps as PDF documents. All services are digital in nature and delivered electronically.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">3. User Accounts</h2>
                <p className="text-muted-foreground leading-relaxed">
                  When you create an account, you must provide accurate and complete information. You are responsible for safeguarding your password and maintaining the confidentiality of your account. You agree to notify us immediately of any unauthorized use of your account.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">4. Credits & Payments</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">Our platform operates on a credit-based system:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Credits are purchased in packs at prices displayed in INR (Indian Rupees)</li>
                  <li>Credits are consumed when downloading PDF plot maps</li>
                  <li>First download of a design is charged at Tier 1 (new plan) rate</li>
                  <li>Subsequent downloads of the same design are charged at Tier 2 (edit) rate</li>
                  <li>All payments are processed securely via Razorpay payment gateway</li>
                  <li>Prices are inclusive of applicable taxes unless stated otherwise</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">5. Use License</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Upon purchasing credits and downloading plot maps, you are granted a non-exclusive, non-transferable license to use the generated PDFs for personal or professional purposes. You may not:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                  <li>Resell, redistribute, or sublicense the platform or its generated outputs</li>
                  <li>Attempt to reverse engineer, decompile, or extract the source code</li>
                  <li>Use the service for any unlawful purpose</li>
                  <li>Share your account credentials with third parties</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">6. Intellectual Property</h2>
                <p className="text-muted-foreground leading-relaxed">
                  All content, features, and functionality of the Platform (excluding user-generated plot data) are owned by {LEGAL_NAME} and are protected by copyright, trademark, and other intellectual property laws. The plot maps generated by users using their own data remain the property of the respective users.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">7. Disclaimer</h2>
                <p className="text-muted-foreground leading-relaxed">
                  The Platform is provided on an "as is" and "as available" basis. While we strive for accuracy, plot maps generated are for reference purposes and should be verified by a licensed surveyor before use in legal or official proceedings. We make no warranties regarding the accuracy, completeness, or suitability of the generated maps for any specific purpose.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">8. Limitation of Liability</h2>
                <p className="text-muted-foreground leading-relaxed">
                  In no event shall {LEGAL_NAME} be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of the Platform, including but not limited to errors in plot maps, loss of data, or business interruption.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">9. Termination</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may terminate or suspend your account without prior notice if you breach these Terms. Upon termination, your right to use the Platform ceases immediately. Any unused credits at the time of termination due to breach may be forfeited.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">10. Governing Law</h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts in Warangal, Telangana.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">11. Changes to Terms</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We reserve the right to modify these Terms at any time. Material changes will be communicated via email or an in-app notification at least 30 days before taking effect.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-foreground mb-4">12. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed">
                  <strong>{LEGAL_NAME}</strong><br />
                  {ADDRESS}<br />
                  Email: <a href={`mailto:${EMAIL}`} className="text-primary hover:underline">{EMAIL}</a><br />
                  Phone: <a href={`tel:${PHONE}`} className="text-primary hover:underline">{PHONE}</a>
                </p>
              </section>
            </div>

            <div className="text-center pt-8 border-t border-border">
              <a href="/" className="inline-flex items-center text-primary hover:underline font-medium">← Back to Home</a>
            </div>
          </div>
        </div>
      </PageLayout.Content>
      <Footer />
    </PageLayout>
  );
};

export default TermsPage;
